// Plays a score timeline with sampled grand piano (smplr), falling back to a small synth.
// Times are in quarter notes from the start of the score; repeats are not expanded.

import type {SplendidGrandPiano} from "smplr";

export interface PlayNote {
    start: number;
    end: number;
    midi: number;
    velocity?: number;
}

export interface Timeline {
    notes: PlayNote[];
    // Quarter-note beats per minute from a position onward
    tempos: Array<{ at: number; bpm: number }>;
    length: number;
    // Start of each measure in quarter notes, when the timeline comes from a score
    measures?: number[];
}

const STEP_PC: Record<string, number> = {C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11};
const BEAT_UNITS: Record<string, number> = {whole: 4, half: 2, quarter: 1, eighth: 0.5, "16th": 0.25};
const DEFAULT_BPM = 100;

function children(el: Element, name: string): Element[] {
    return Array.from(el.children).filter(c => c.tagName === name);
}

function child(el: Element, name: string): Element | undefined {
    return children(el, name)[0];
}

function num(el: Element | undefined, name: string, fallback = 0): number {
    const value = parseFloat((el && child(el, name)?.textContent) ?? "");
    return Number.isFinite(value) ? value : fallback;
}

function directionTempo(direction: Element): number | null {
    const sound = child(direction, "sound")?.getAttribute("tempo");
    if (sound && parseFloat(sound) > 0) return parseFloat(sound);
    const metronome = direction.getElementsByTagName("metronome")[0];
    const perMinute = metronome && num(metronome, "per-minute");
    const unit = metronome && BEAT_UNITS[child(metronome, "beat-unit")?.textContent?.trim() ?? ""];
    if (!perMinute || !unit) return null;
    return perMinute * unit * (child(metronome, "beat-unit-dot") ? 1.5 : 1);
}

export function buildTimeline(doc: Document): Timeline {
    const notes: PlayNote[] = [];
    const tempoAt = new Map<number, number>();
    const measures: number[] = [];
    let length = 0;

    for (const part of children(doc.documentElement, "part")) {
        let divisions = 1;
        let measureStart = 0;
        const openTies = new Map<string, PlayNote>();

        const partMeasures = children(part, "measure");
        for (let index = 0; index < partMeasures.length; index++) {
            const measure = partMeasures[index];
            if (measures.length <= index) measures.push(measureStart);
            let cursor = 0;
            let lastOnset = 0;
            let measureLength = 0;

            for (const el of Array.from(measure.children)) {
                switch (el.tagName) {
                    case "attributes":
                        divisions = num(el, "divisions", divisions) || divisions;
                        break;
                    case "direction": {
                        const bpm = directionTempo(el);
                        const at = measureStart + cursor / divisions;
                        if (bpm && !tempoAt.has(at)) tempoAt.set(at, bpm);
                        break;
                    }
                    case "sound": {
                        const bpm = parseFloat(el.getAttribute("tempo") ?? "");
                        const at = measureStart + cursor / divisions;
                        if (bpm > 0 && !tempoAt.has(at)) tempoAt.set(at, bpm);
                        break;
                    }
                    case "backup":
                        cursor -= num(el, "duration");
                        break;
                    case "forward":
                        cursor += num(el, "duration");
                        break;
                    case "note": {
                        const duration = num(el, "duration");
                        let onset = lastOnset;
                        if (!child(el, "chord")) {
                            onset = lastOnset = cursor;
                            if (!child(el, "grace")) cursor += duration;
                        }
                        const pitch = child(el, "pitch");
                        if (!pitch || child(el, "grace") || child(el, "cue")) break;

                        const step = child(pitch, "step")?.textContent?.trim() ?? "C";
                        const midi = 12 * (num(pitch, "octave", 4) + 1) + STEP_PC[step] + Math.round(num(pitch, "alter"));
                        const start = measureStart + onset / divisions;
                        const end = measureStart + (onset + duration) / divisions;
                        const tieKey = `${midi}:${child(el, "voice")?.textContent ?? ""}`;
                        const ties = children(el, "tie").map(t => t.getAttribute("type"));
                        const open = openTies.get(tieKey);

                        if (ties.includes("stop") && open) {
                            open.end = end;
                            if (!ties.includes("start")) openTies.delete(tieKey);
                        } else {
                            const played = {start, end, midi};
                            notes.push(played);
                            if (ties.includes("start")) openTies.set(tieKey, played);
                        }
                        break;
                    }
                }
                measureLength = Math.max(measureLength, cursor);
            }
            measureStart += measureLength / divisions;
        }
        length = Math.max(length, measureStart);
    }

    const changes = Array.from(tempoAt, ([at, bpm]) => ({at, bpm})).sort((a, b) => a.at - b.at);
    const initial = changes.find(c => c.at === 0)?.bpm ?? changes[0]?.bpm ?? DEFAULT_BPM;
    return {
        notes: notes.sort((a, b) => a.start - b.start),
        tempos: [{at: 0, bpm: initial}, ...changes.filter(c => c.at > 0)],
        length,
        measures,
    };
}

// Piecewise-linear map between score quarters and seconds
export class TempoMap {
    private readonly starts: number[] = [];

    constructor(private readonly tempos: Timeline["tempos"]) {
        let seconds = 0;
        tempos.forEach((t, i) => {
            if (i > 0) seconds += (t.at - tempos[i - 1].at) * 60 / tempos[i - 1].bpm;
            this.starts.push(seconds);
        });
    }

    toSeconds(quarters: number): number {
        let i = this.tempos.length - 1;
        while (i > 0 && this.tempos[i].at > quarters) i--;
        return this.starts[i] + (quarters - this.tempos[i].at) * 60 / this.tempos[i].bpm;
    }

    toQuarters(seconds: number): number {
        let i = this.starts.length - 1;
        while (i > 0 && this.starts[i] > seconds) i--;
        return this.tempos[i].at + (seconds - this.starts[i]) * this.tempos[i].bpm / 60;
    }
}

// One AudioContext and one sampled piano for the whole page, so the samples load once
let sharedContext: AudioContext | undefined;
let pianoPromise: Promise<SplendidGrandPiano | null> | undefined;

export interface PianoStatus {
    state: "idle" | "loading" | "ready" | "fallback";
    // Share of samples loaded, 0..1
    progress: number;
}

let pianoStatus: PianoStatus = {state: "idle", progress: 0};
const statusListeners = new Set<(status: PianoStatus) => void>();

function setPianoStatus(next: PianoStatus) {
    pianoStatus = next;
    statusListeners.forEach(l => l(next));
}

export function subscribePianoStatus(listener: (status: PianoStatus) => void): () => void {
    statusListeners.add(listener);
    listener(pianoStatus);
    return () => statusListeners.delete(listener);
}

// Starts fetching samples before the first play; the context stays suspended until a click resumes it
export function preloadPiano() {
    void loadPiano(audioContext());
}

function audioContext(): AudioContext {
    sharedContext ??= new AudioContext();
    return sharedContext;
}

// Only the mp and mf layers are fetched to keep the download small; velocities are squeezed into them
const PIANO_VELOCITY: [number, number] = [68, 100];

function loadPiano(ctx: AudioContext): Promise<SplendidGrandPiano | null> {
    pianoPromise ??= (async () => {
        setPianoStatus({state: "loading", progress: 0});
        try {
            const {SplendidGrandPiano} = await import("smplr");
            const piano = new SplendidGrandPiano(ctx, {
                notesToLoad: {velocityRange: PIANO_VELOCITY},
                onLoadProgress: p => setPianoStatus({state: "loading", progress: p.total ? p.loaded / p.total : 0}),
            });
            await piano.load;
            setPianoStatus({state: "ready", progress: 1});
            return piano;
        } catch (e) {
            // Without the samples (offline, blocked CDN) the simple synth below still plays
            console.error(e);
            setPianoStatus({state: "fallback", progress: 0});
            return null;
        }
    })();
    return pianoPromise;
}

function pianoVelocity(velocity: number): number {
    const [low, high] = PIANO_VELOCITY;
    return Math.round(low + (Math.min(127, Math.max(1, velocity)) / 127) * (high - low));
}

function playTone(ctx: AudioContext, out: AudioNode, midi: number, when: number, duration: number) {
    const frequency = 440 * 2 ** ((midi - 69) / 12);
    // Low strings ring longer than high ones
    const decay = Math.max(0.35, 1.6 - (midi - 40) / 40);
    const off = when + Math.max(duration, 0.05);
    // Oscillators stop once the tone has faded instead of running for the whole held note
    const end = Math.min(off + 0.4, when + decay * 5);

    const envelope = ctx.createGain();
    envelope.gain.setValueAtTime(0, when);
    envelope.gain.linearRampToValueAtTime(0.25, when + 0.006);
    envelope.gain.setTargetAtTime(0, when + 0.006, decay);
    envelope.gain.setTargetAtTime(0, off, 0.07);
    envelope.connect(out);

    for (const [harmonic, level] of [[1, 1], [2, 0.3]] as const) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = harmonic === 1 ? "triangle" : "sine";
        osc.frequency.value = frequency * harmonic;
        gain.gain.value = level;
        osc.connect(gain).connect(envelope);
        osc.start(when);
        osc.stop(end);
    }
}

const LOOKAHEAD = 0.25;

export interface PlayerEvents {
    onPosition: (quarters: number) => void;
    onEnd: () => void;
    // Share of the piano samples loaded, or null once loading is over
    onLoading: (progress: number | null) => void;
}

export class ScorePlayer {
    private piano: SplendidGrandPiano | null = null;
    private synthOut?: GainNode;
    private timer?: number;
    private frame?: number;
    // Bumped by every play/stop so a play still waiting for samples can tell it was cancelled
    private session = 0;
    private startCtxTime = 0;
    private startSeconds = 0;
    private speed = 1;
    private nextNote = 0;
    private readonly tempo: TempoMap;
    private readonly noteSeconds: Array<{ start: number; end: number; midi: number; velocity: number }>;
    readonly totalSeconds: number;

    constructor(private readonly timeline: Timeline, private readonly events: PlayerEvents) {
        this.tempo = new TempoMap(timeline.tempos);
        this.noteSeconds = timeline.notes.map(n => ({
            start: this.tempo.toSeconds(n.start),
            end: this.tempo.toSeconds(n.end),
            midi: n.midi,
            velocity: n.velocity ?? 80,
        }));
        this.totalSeconds = this.tempo.toSeconds(timeline.length);
    }

    secondsOf(quarters: number) {
        return this.tempo.toSeconds(quarters);
    }

    quartersOf(seconds: number) {
        return this.tempo.toQuarters(seconds);
    }

    get playing() {
        return this.timer !== undefined;
    }

    // Resolves false when stop() came first, e.g. while the samples were still loading
    async play(fromSeconds: number, speed: number): Promise<boolean> {
        this.stop();
        const session = this.session;
        this.startSeconds = Math.max(0, Math.min(fromSeconds, this.totalSeconds));
        this.speed = speed;

        const ctx = audioContext();
        await ctx.resume();
        const unsubscribe = subscribePianoStatus(status => {
            if (session === this.session && status.state === "loading") this.events.onLoading(status.progress);
        });
        this.piano = await loadPiano(ctx);
        unsubscribe();
        if (session !== this.session) return false;
        this.events.onLoading(null);

        if (!this.piano) {
            const compressor = ctx.createDynamicsCompressor();
            compressor.connect(ctx.destination);
            this.synthOut = ctx.createGain();
            this.synthOut.gain.value = 0.8;
            this.synthOut.connect(compressor);
        }

        this.startCtxTime = ctx.currentTime + 0.08;
        this.nextNote = this.noteSeconds.findIndex(n => n.start >= this.startSeconds - 1e-6);
        if (this.nextNote < 0) this.nextNote = this.noteSeconds.length;

        this.schedule();
        this.timer = window.setInterval(() => this.schedule(), 25);
        const tick = () => {
            const seconds = this.position();
            this.events.onPosition(this.tempo.toQuarters(seconds));
            if (seconds >= this.totalSeconds) {
                this.stop();
                this.events.onEnd();
                return;
            }
            this.frame = requestAnimationFrame(tick);
        };
        this.frame = requestAnimationFrame(tick);
        return true;
    }

    // Score seconds at the current moment
    position(): number {
        if (!sharedContext || !this.playing) return this.startSeconds;
        return this.startSeconds + Math.max(0, sharedContext.currentTime - this.startCtxTime) * this.speed;
    }

    stop() {
        if (this.playing) this.startSeconds = this.position();
        this.session++;
        window.clearInterval(this.timer);
        this.timer = undefined;
        if (this.frame !== undefined) cancelAnimationFrame(this.frame);
        this.frame = undefined;
        this.piano?.stop();
        if (sharedContext && this.synthOut) {
            const out = this.synthOut;
            out.gain.setTargetAtTime(0, sharedContext.currentTime, 0.02);
            window.setTimeout(() => out.disconnect(), 300);
        }
        this.synthOut = undefined;
    }

    private schedule() {
        const ctx = sharedContext;
        if (!ctx) return;
        const horizon = this.startSeconds + (ctx.currentTime + LOOKAHEAD - this.startCtxTime) * this.speed;
        while (this.nextNote < this.noteSeconds.length && this.noteSeconds[this.nextNote].start <= horizon) {
            const note = this.noteSeconds[this.nextNote++];
            const when = Math.max(ctx.currentTime, this.startCtxTime + (note.start - this.startSeconds) / this.speed);
            const duration = Math.max(0.05, (note.end - note.start) / this.speed);
            if (this.piano) {
                this.piano.start({note: note.midi, velocity: pianoVelocity(note.velocity), time: when, duration});
            } else if (this.synthOut) {
                playTone(ctx, this.synthOut, note.midi, when, duration);
            }
        }
    }
}

export function formatTime(seconds: number): string {
    const s = Math.max(0, Math.floor(seconds));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
